import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileDown, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { ScoutingView } from "../components/scouting/ScoutingView";
import { AddPlayerPanel } from "../components/modals/AddPlayerPanel";
import EditBatterPanel from "../components/modals/EditBatterPanel";
import EditPitcherPanel from "../components/modals/EditPitcherPanel";
import ScoutingReportManager from "../managers/ScoutingReportsManager";
import AuthManager from "../managers/AuthManager";
import { LoadingState } from "../components/alerts/Alerts";
import { fetchAPI, fetchAPIBlob } from "../config/api";
import { toDivisionApi } from "../config/constants";
import { ReportHeader } from "../components/reports";

export const ScoutingReport = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [availablePitchers, setAvailablePitchers] = useState([]);
  
  // Add panels
  const [isAddPlayerPanelOpen, setIsAddPlayerPanelOpen] = useState(false);
  const [isAddPitcherPanelOpen, setIsAddPitcherPanelOpen] = useState(false);
  
  // Edit panels (separate from add)
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editingPitcher, setEditingPitcher] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const unsubscribeAuth = AuthManager.onAuthStateChanged(async (user) => {
        if (!isMounted) return;

        try {
          if (!user) {
            const result = await AuthManager.anonymousSignIn();
            if (result.success && isMounted) {
              user = result.user;
            }
          }

          if (user && reportId) {
            const fetchedReport = await ScoutingReportManager.getReportById(reportId);
            if (isMounted && fetchedReport) {
              setReport(fetchedReport);
            } else if (isMounted) {
              toast.error("Report not found");
              navigate('/scouting');
            }
          }
        } catch (err) {
          console.error("Error loading report:", err);
          toast.error("Failed to load report");
        } finally {
          if (isMounted) {
            setLoading(false);
            setIsAuthReady(true);
          }
        }
      });

      return unsubscribeAuth;
    };

    const cleanup = initializeAuth();
    return () => {
      isMounted = false;
      cleanup.then((unsubscribe) => unsubscribe());
    };
  }, [reportId, navigate]);

  const fetchPlayers = async (teamName, division, year) => {
    try {
      // First, get the team_id from the teams list
      const teams = await fetchAPI(`/api/teams?division=${toDivisionApi(division)}&year=${year}`);
      const team = teams?.find(t => t.team_name === teamName);
      
      if (!team?.team_id) {
        console.error("Team not found or missing team_id");
        return;
      }

      const response = await fetchAPI(
        `/api/team/roster?team_id=${team.team_id}&division=${toDivisionApi(division)}&year=${year}`
      );
      if (response?.roster) {
        const batters = response.roster
          .filter(p => !['P', 'RHP', 'LHP'].includes(p.position?.toUpperCase()))
          .map(p => ({
            playerId: p.player_id,
            name: p.player_name,
            position: p.position || "",
          }));
        setAvailablePlayers(batters);
      }
    } catch (err) {
      console.error("Error fetching players:", err);
    }
  };

  const fetchPitchers = async (teamName, division, year) => {
    try {
      const teams = await fetchAPI(`/api/teams?division=${toDivisionApi(division)}&year=${year}`);
      const team = teams?.find(t => t.team_name === teamName);
      
      if (!team?.team_id) {
        console.error("Team not found or missing team_id");
        return;
      }

      const response = await fetchAPI(
        `/api/team/roster?team_id=${team.team_id}&division=${toDivisionApi(division)}&year=${year}`
      );
      if (response?.roster) {
        const pitchers = response.roster
          .filter(p => ['P', 'RHP', 'LHP'].includes(p.position?.toUpperCase()))
          .map(p => ({
            playerId: p.player_id,
            name: p.player_name,
            position: p.position || "",
          }));
        setAvailablePitchers(pitchers);
      }
    } catch (err) {
      console.error("Error fetching pitchers:", err);
    }
  };

  const handleUpdateReport = async (updatedReport) => {
    setReport(updatedReport);
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportPDF = async () => {
    if (isExporting || !report) return;
    setIsExporting(true);
    const loadingToast = toast.loading("Generating PDF...");
    try {
      const latestReport = await ScoutingReportManager.getReportById(report.id);
      const reportToExport = latestReport || report;
      const blob = await fetchAPIBlob('/reports/scouting_report', {
        method: 'POST',
        body: JSON.stringify({ report_id: reportToExport.id, report_data: reportToExport }),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scouting_report_${report.teamId || 'report'}_${report.year}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!", { id: loadingToast });
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export PDF", { id: loadingToast });
    } finally {
      setIsExporting(false);
    }
  };

  const openAddPlayerPanel = () => {
    if (!report) return;
    fetchPlayers(report.teamName, report.division, report.year);
    setIsAddPlayerPanelOpen(true);
  };

  const openAddPitcherPanel = () => {
    if (!report) return;
    fetchPitchers(report.teamName, report.division, report.year);
    setIsAddPitcherPanelOpen(true);
  };

  const handleAddPlayer = async (playerData) => {
    if (!report) return;
    
    const loadingToast = toast.loading("Adding player...");
    try {
      const newPlayer = {
        ...playerData,
        id: playerData.playerId || `custom-${Date.now()}`,
      };
      const updatedReport = {
        ...report,
        positionPlayers: [...(report.positionPlayers || []), newPlayer],
        numHitters: (report.numHitters || 0) + 1,
      };
      await ScoutingReportManager.updateReport(report.id, updatedReport);
      setReport(updatedReport);
      setIsAddPlayerPanelOpen(false);
      toast.success("Player added", { id: loadingToast });
    } catch (err) {
      console.error("Error adding player:", err);
      toast.error("Failed to add player", { id: loadingToast });
    }
  };

  const handleAddPitcher = async (pitcherData) => {
    if (!report) return;
    
    const loadingToast = toast.loading("Adding pitcher...");
    try {
      const newPitcher = {
        ...pitcherData,
        id: pitcherData.playerId || `custom-${Date.now()}`,
      };
      const updatedReport = {
        ...report,
        pitchers: [...(report.pitchers || []), newPitcher],
        numPitchers: (report.numPitchers || 0) + 1,
      };
      await ScoutingReportManager.updateReport(report.id, updatedReport);
      setReport(updatedReport);
      setIsAddPitcherPanelOpen(false);
      toast.success("Pitcher added", { id: loadingToast });
    } catch (err) {
      console.error("Error adding pitcher:", err);
      toast.error("Failed to add pitcher", { id: loadingToast });
    }
  };

  const handleEditPlayer = async (playerData) => {
    if (!report || !editingPlayer) return;
    
    const loadingToast = toast.loading("Updating player...");
    try {
      const updatedPlayers = report.positionPlayers.map(p =>
        p.id === editingPlayer.id ? { ...p, ...playerData } : p
      );
      const updatedReport = { ...report, positionPlayers: updatedPlayers };
      await ScoutingReportManager.updateReport(report.id, updatedReport);
      setReport(updatedReport);
      setEditingPlayer(null);
      toast.success("Player updated", { id: loadingToast });
    } catch (err) {
      console.error("Error updating player:", err);
      toast.error("Failed to update player", { id: loadingToast });
    }
  };

  const handleEditPitcher = async (pitcherData) => {
    if (!report || !editingPitcher) return;
    
    const loadingToast = toast.loading("Updating pitcher...");
    try {
      const updatedPitchers = (report.pitchers || []).map(p =>
        p.id === editingPitcher.id ? { ...p, ...pitcherData } : p
      );
      const updatedReport = { ...report, pitchers: updatedPitchers };
      await ScoutingReportManager.updateReport(report.id, updatedReport);
      setReport(updatedReport);
      setEditingPitcher(null);
      toast.success("Pitcher updated", { id: loadingToast });
    } catch (err) {
      console.error("Error updating pitcher:", err);
      toast.error("Failed to update pitcher", { id: loadingToast });
    }
  };

  if (!isAuthReady || loading) {
    return <LoadingState />;
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Report not found</p>
          <button
            onClick={() => navigate('/scouting')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <ReportHeader
          teamId={report.teamId}
          teamName={report.teamName}
          reportType="Scouting Report"
          year={report.year}
        >
          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isExporting ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
            {isExporting ? "Generating…" : "Export PDF"}
          </button>
        </ReportHeader>
        <div className="relative z-10">
          <ScoutingView
            report={report}
            onUpdateReport={handleUpdateReport}
            onAddPlayer={openAddPlayerPanel}
            onAddPitcher={openAddPitcherPanel}
            onEditPlayer={(player) => setEditingPlayer(player)}
            onEditPitcher={(pitcher) => setEditingPitcher(pitcher)}
            onViewPlayer={(player) => navigate(`/player/${player.id}`)}
            onViewPitcher={(pitcher) => navigate(`/player/${pitcher.id}`)}
          />
        </div>

        <AddPlayerPanel
          isOpen={isAddPlayerPanelOpen}
          onClose={() => setIsAddPlayerPanelOpen(false)}
          onSubmit={handleAddPlayer}
          availablePlayers={availablePlayers}
          isPitcher={false}
        />

        <AddPlayerPanel
          isOpen={isAddPitcherPanelOpen}
          onClose={() => setIsAddPitcherPanelOpen(false)}
          onSubmit={handleAddPitcher}
          availablePlayers={availablePitchers}
          isPitcher={true}
        />

        <EditBatterPanel
          isOpen={!!editingPlayer}
          onClose={() => setEditingPlayer(null)}
          onSubmit={handleEditPlayer}
          editingPlayer={editingPlayer}
        />

        {/* Edit Pitcher Panel */}
        <EditPitcherPanel
          isOpen={!!editingPitcher}
          onClose={() => setEditingPitcher(null)}
          onSubmit={handleEditPitcher}
          editingPlayer={editingPitcher}
        />
      </div>
    </div>
  );
};
